import {useState, useEffect} from 'react';
import { debounce} from 'lodash';
import { useErrorBoundary } from 'react-error-boundary';
import { Divider, Grid2 as Grid, Typography, Alert, Stack, FormControl, OutlinedInput, InputAdornment, InputLabel, Select, MenuItem, CircularProgress, Box, useMediaQuery, useTheme, styled} from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import {Search} from '@mui/icons-material';

import { PeriodicReportColumns } from '../../../types/referenceData/reportTableColumns';
import PeriodicReport from '../../../types/reports/periodicReportContent';
import { DataGrid } from '@mui/x-data-grid';
import ReportListExportSession from './ReportListExportSection';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';
import { ReportTypeEnums } from '../../../types/referenceData/reportTypeEnums';

const StyledDataGrid = styled(DataGrid)(() => ({
  '& .even-row': {
    backgroundColor: '#f9f9f9',
  },
  '& .odd-row': {
    backgroundColor: '#ffffff',
  },
}));


const LpcReports = (): JSX.Element => {
    
    document.title = 'OCPF - Local Party Committee Reports';

    const { showBoundary } = useErrorBoundary();  
    const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() - index);

    const theme = useTheme(); 
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const [isLoading, setIsLoading] = useState(true);
    const [tableColumns, setColumns] = useState(PeriodicReportColumns);
    const [tableRows, setRows] = useState<PeriodicReport[]>();
    const [reports, setReports] = useState<PeriodicReport[]>();
    const [reportsSummary, setReportsSummary] = useState('No reports found.');
    
    // LPC pre primary report filed around August in election year, otherwise the year-end report is considered.
    const [reportYear, setReportYear] = useState(() => {
        if (new Date().getFullYear() % 2 === 0 ) {
          return new Date().getMonth() <= 6 ? new Date().getFullYear() - 2 : new Date().getFullYear(); 
        }
    
        return new Date().getFullYear() - 1;
      });
    
    const [reportType, setReportType] = useState(() => {
        if (new Date().getFullYear() % 2 === 0 ) {
          return new Date().getMonth() <= 6 ? ReportTypeEnums.LpcYearEnd : ReportTypeEnums.LpcPrePrimary;
        }
    
        return ReportTypeEnums.LpcYearEnd;
      });
      
    const [partyAffiliation, setPartyAffiliation] = useState('D');
    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');

    useEffect(() => {
        getLpcReportList();
      }, [reportYear, reportType, partyAffiliation])

    const getLpcReportList = async () => {

        setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/lpc/excelOutput/${reportYear}`);
        setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/lpc/textOutput/${reportYear}`);

        let params = [
            `reportTypeId=${reportType}`,
            `partyAffiliation=${partyAffiliation}`,
            `pageSize=${1000}`
          ].join('&');

        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/lpc/${reportYear}?${params}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the LPC reports.'));
            }
            return result.json();
          })
          .then(result => {
            let count = result.length;
            if (count === 0){
                setReportsSummary("No reports found.");
                setRows([]);
            }
            else{
                setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
                setRows(result);
                setReports(result);
            }
            setIsLoading(false);
          });
        }catch(error){
            console.error('Uncaught Error:', error);
            showBoundary(new Error(`${error}`));
        }
      }
      
      const updateReporYear = (event: any) => {
        setReportYear(event.target.value);
      };

      const updateReporType = (event: any) => {
        setReportType(event.target.value);
      };

      const updatePartyAffiliation = (event: any) => {
        setPartyAffiliation(event.target.value);
      };

      const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        if(searchInput.length === 0){
          setRows(reports);
        }
        else{
          let filteredRows = reports?.filter((row) => row.filerName.toLowerCase().includes(searchInput)) || [];
          let count = filteredRows.length;
          setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
          setRows(filteredRows);
        }
      }, 800);

    return (
      <Main>
        <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            gutterBottom
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            marginBottom={2}
            >
            Local Party Committee Reports
        </Typography>
        <Divider/>
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={{ xs: 12, md: 10 }}>
            <Stack direction={"row"} spacing={1} 
                sx={{ mb: 1}}
                >
                 <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id="year-id">Year</InputLabel>
                    <Select 
                        labelId="year-id"
                        id="year-select"
                        aria-labelledby='year-id'
                        sx={{fontSize: 'smaller'}} 
                        label="Report Year"
                        value={reportYear}
                        onChange={updateReporYear}
                    >
                    {
                        dropDownYears.map((year) =>
                            <MenuItem value={year} key={year} >{year}</MenuItem>
                        )
                    }
                    </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id="party-id">Party</InputLabel>
                    <Select 
                        labelId="party-id"
                        id="party-select"
                        aria-labelledby='party-id'
                        sx={{fontSize: 'smaller'}} 
                        label="Type"
                        value={partyAffiliation}
                        onChange={updatePartyAffiliation}
                    >
                        <MenuItem value={'D'} >Democratic</MenuItem>
                        <MenuItem value={'R'} >Republican</MenuItem>
                        <MenuItem value={'O'} >Other</MenuItem>
                    </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id="type-id">Type</InputLabel>
                    <Select 
                        labelId="type-id"
                        id="type-select"
                        aria-labelledby='type-id'
                        sx={{fontSize: 'smaller'}} 
                        label="Type"
                        value={reportType}
                        onChange={updateReporType}
                    >
                        <MenuItem value={-1} >Composite</MenuItem>
                        <MenuItem value={50} >Pre-Primary</MenuItem>
                        <MenuItem value={51} >Pre-Election</MenuItem>
                        <MenuItem value={52} >Year-End</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 200 }}>
                        <OutlinedInput
                            aria-label='filter by lpc name'
                            placeholder={"Filter by LPC name"}
                            startAdornment={
                                <InputAdornment position="start">
                                <Search />
                                </InputAdornment>
                            }
                            onChange={filterByName}
                            sx={{ fontSize: 'smaller' }}
                        />

                </FormControl>
            </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <ReportListExportSession excelExportUrl={excelExportUrl} txtExportUrl={txtExportUrl}></ReportListExportSession>
        </Grid>
        <Grid size={11.5}>
            <Alert icon={false} severity="info"
            sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                '.MuiAlert-message': { 
                padding: '4px',
                } 
            }}
        >
            {reportsSummary}
            </Alert>
        </Grid>
      </Grid>
       {/*report table*/}
       <Box 
        sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: '95%',
        '.MuiDataGrid-columnHeaderTitle': { 
            fontWeight: 'bold !important',
        },
        }}>
        <StyledDataGrid
            rows={tableRows}
            columnVisibilityModel={{
              filed: false,
              cpfId: isSM?false:true,
              filerName: isSM?false:true,
              filerNameWithId: isSM?true:false,
              reportYear: isSM?false:true,
              reportTypeShortDescription: isSM?false:true,
              reportYearAndType: isSM?true:false
            }}
            getRowHeight={isSM? ()=> 'auto': () => 40}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
              ? 'even-row'
              : 'odd-row'
            }
            columns={tableColumns}
            getRowId={(row) => row.cpfId}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            sx={{ 
              '.MuiDataGrid-cell': {
                whiteSpace: 'normal', 
                wordBreak: 'break-word',
                //paddingTop: '3px!important',
                //paddingBottom: '3px!important',
              },
            }}
        />
      </Box>
       {/*loading indicator*/}
       <CircularProgress
            aria-label='loading'
            size={50}
            className={isLoading? "": "hidden"}
            sx={{
              position: "fixed",
              left: "50%",
              top: "20%",
              transform: "translate(-50%, -50%)",
              zIndex: 2
             }}
        />
    </Container>
    </Main>
  );
};

export default LpcReports;
